import { useContext, useEffect, useState } from "react";
import { css } from "@emotion/react";
import { Box, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";

import type { Dayjs } from "@thalamos/common";
import type {
  ExtendedThalamosUser,
  ExtendedWorkItem,
  WorkItemAction,
} from "@thalamos/aspire-common";
import {
  getFullFormName,
  isGuestUser,
  isSoadUserSession,
} from "@thalamos/aspire-common";

import { LoggedInUserContext } from "../../Contexts.js";
import { useRetriveWorkItems } from "../../hooks/apiCalls.js";
import { routeFns } from "../../routePaths.js";
import { breakpoints } from "../../styles/themeCommon.js";
import CaseloadUnavailable from "./CaseloadUnavailable.js";
import NoResultsBanners from "./NoResultsBanners.js";
import PaginationControls from "./PaginationControls.js";
import { QuickCreateSoadAssessment } from "./QuickCreateSoadAssessment.js";
import RenderedTable from "./RenderedTable.js";
import SearchAndFilters from "./SearchAndFilters.js";
import type { SelectedFilteredOptions, WorkMode } from "./types.js";
import WorkTabs from "./WorkTabs.js";
import {
  Banner,
  BannerList,
  Button,
  LoadingSpinner,
} from "~/components/design-system/index.js";
import { isDevEnv } from "~/config.js";

const pageLimit = 30;

export function CaseloadPage() {
  const theme = useTheme();

  const userContext = useContext(LoggedInUserContext);
  const { user } = userContext!;

  const isSoad = isSoadUserSession(user);

  const contextId = (
    user.sessionContext?.type === "team"
      ? user.sessionContext.teamId
      : user.sessionContext?.organisationId
  )!;

  const [page, setPage] = useState(0);
  const [patientSearchFilter, setPatientSearchFilter] = useState("");
  const [fromDate, setFromDate] = useState<Dayjs | null>(null);
  const [toDate, setToDate] = useState<Dayjs | null>(null);
  const [selectedOptions, setSelectedOptions] = useState<string[]>([]);
  const [selectedFilteredOptions, setSelectedFilteredOptions] =
    useState<SelectedFilteredOptions>({});

  const [mode, setMode] = useState<WorkMode>("caseload");

  const [selectedTab, setSelectedTab] = useState(0);

  const { pathname } = useLocation();
  const { t } = useTranslation();

  useEffect(() => {
    switch (pathname) {
      case routeFns.caseloadUser():
        setMode("user-caseload");
        break;
      case routeFns.caseloadCaseload():
        setMode("caseload");
        break;
      case routeFns.caseloadArchive():
        setMode("archive");
        break;
    }
  }, [pathname]);

  const userSpecifiedFromDate = fromDate?.isValid()
    ? fromDate?.tz("Europe/London").format("YYYY-MM-DD")
    : undefined;

  const { work, workLoading, workFailure, retryRetrieveWork } =
    useRetriveWorkItems({
      userId: user.id,
      contextId,
      mode,
      pageLimit,
      page,
      patientSearchFilter,
      selectedFilteredOptions,
      userSpecifiedFromDate,
      toDate,
    });

  const showMyCaseload = !isSoad;

  const selectedWork = work
    ? mode === "caseload"
      ? work.caseload
      : mode === "user-caseload"
        ? work.myCaseload
        : work.archive
    : null;

  const noSearchOrFilterResults =
    !!patientSearchFilter || !!fromDate || !!toDate || !!selectedOptions.length;

  useEffect(() => {
    setPage(0);
  }, [patientSearchFilter, selectedFilteredOptions, fromDate, toDate]);

  return (
    <Box sx={{ borderBottom: 0, borderColor: "divider" }}>
      <Box
        css={css`
          @media (min-width: ${breakpoints.values.sm}px) {
            position: relative;
            bottom: ${theme.spacing(1.05)};
            height: ${theme.spacing(0.375)};
            background-color: ${theme.palette.common.background};
          }
        `}
      />

      <SearchAndFilters
        user={user}
        patientSearchFilter={patientSearchFilter}
        formFullName={getFullFormName}
        selectedOptions={selectedOptions}
        setPatientSearchFilter={setPatientSearchFilter}
        fromDate={fromDate}
        setFromDate={setFromDate}
        toDate={toDate}
        work={work}
        setToDate={setToDate}
        selectedTab={selectedTab}
        setSelectedTab={setSelectedTab}
        setSelectedFilteredOptions={setSelectedFilteredOptions}
        setSelectedOptions={setSelectedOptions}
      />
      <WorkTabs
        mode={mode}
        work={work}
        user={user}
        setPage={() => setPage(0)}
        showMyCaseload={showMyCaseload}
      />

      {isGuestUser(user) && mode === "archive" && (
        <Box sx={{ mb: 2 }}>
          <Banner
            bannerType={BannerList.WARNING}
            title={t("pages.caseload.guestUserBanner")}
          />
        </Box>
      )}
      {workLoading ? (
        <Box sx={{ width: "100%", display: "flex", justifyContent: "center" }}>
          <LoadingSpinner />
        </Box>
      ) : workFailure !== null ? (
        <CaseloadUnavailable
          reason={workFailure.reason}
          onRetry={retryRetrieveWork}
        />
      ) : (
        <CaseloadPageInner
          page={page}
          setPage={setPage}
          user={user}
          work={{
            senderNames: work?.senderNames || [],
            work: selectedWork?.work || [],
            count: selectedWork?.count || 0,
          }}
          noSearchOrFilterResults={noSearchOrFilterResults}
          mode={mode}
        />
      )}
    </Box>
  );
}

function CaseloadPageInner({
  user,
  page,
  setPage,
  work,
  mode,
  noSearchOrFilterResults,
}: {
  user: ExtendedThalamosUser;
  page: number;
  setPage: (page: number) => void;
  work: {
    senderNames: { username: string }[];
    work: (ExtendedWorkItem & { actions: WorkItemAction[] })[];
    count: number;
  };
  mode: WorkMode;
  noSearchOrFilterResults: boolean;
}) {
  const isSoad = isSoadUserSession(user);
  const navigate = useNavigate();

  const { t } = useTranslation();

  const numPages = Math.ceil(work?.count / pageLimit);

  const isNhsNumberConfigEnabled =
    user.sessionOrganisationConfiguration?.nhsNumberEnabled ?? false;

  return (
    <>
      {isSoad && mode === "caseload" && (
        <Box sx={{ mt: 2 }}>
          <Button
            label={t("pages.caseload.beginSoadAssessment")}
            onClick={() => {
              navigate(routeFns.newSoadAssessment());
            }}
          />
        </Box>
      )}
      {isDevEnv && isSoad && mode === "caseload" && (
        <QuickCreateSoadAssessment />
      )}
      {!!work?.count ? (
        <>
          <PaginationControls
            numPages={numPages}
            page={page}
            setPage={setPage}
          />

          <RenderedTable
            work={work}
            showNhsNumber={isNhsNumberConfigEnabled}
            user={user}
            mode={mode}
          />
          <PaginationControls
            numPages={numPages}
            page={page}
            setPage={setPage}
          />
        </>
      ) : (
        <NoResultsBanners
          mode={mode}
          noSearchOrFilterResults={noSearchOrFilterResults}
          user={user}
        />
      )}
    </>
  );
}
